/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {
  ChangeEvent,
  useCallback,
  useMemo
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {Device} from '@ion/core/src/types/entities';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {MAX_VIEW_DEVICES} from './BulkViewDevicesDialog';
import {getDeviceTab} from './BulkViewDeviceTabs';
import {
  removeDevicesTab,
  setSelectedDevicesTab,
  updateDevicesTab
} from './state';
import {viewDevicesSelectedTabSelector, viewDevicesTabsSelector} from './state-selectors';

/**
 * Defines useBulkViewDeviceTabs hook for handling view devices tabs selection, deselection,
 * adding, and deleting related tabs logic for multi device tabs.
 *
 * ```typescript
 *
 *  import {useBulkViewDeviceTabs} from ' ./useBulkViewDeviceTabs';
 *
 *  const operations = useBulkViewDeviceTabs();
 *
 * ```
 */
export const useBulkViewDeviceTabs = () => {
  const dispatch    = useDispatch();
  const tabItems    = useSelector(viewDevicesTabsSelector);
  const selectedTab = useSelector(viewDevicesSelectedTabSelector);

  const selectedIndex = useMemo(() => {
    let index: number;
    tabItems?.some(({data}, i) => {
      index = i;
      return data?.id === selectedTab?.id;
    });
    return index;
  }, [tabItems, selectedTab]);

  const handleOnChange = useCallback((e: ChangeEvent, value: number) => {
    e?.preventDefault();
    const selected = tabItems[value]?.data;
    dispatch(setSelectedDevicesTab(selected));
  }, [tabItems]);

  const onCloseTab = useCallback((e: ChangeEvent, value: number) => {
    e?.preventDefault();
    const tab = tabItems[value]?.data;
    const tabLen = tabItems?.length;
    dispatch(removeDevicesTab(tab));
    if (selectedTab?.id === tab?.id) {
      const index = (value + 1) < tabLen ? value + 1 : 0;
      dispatch(setSelectedDevicesTab(tabItems[index]?.data));
    }
  }, [tabItems, selectedTab]);

  const getIdentifierExists = useCallback((identifier: string | number) => {
    return tabItems.find(tab => tab?.data.id === identifier);
  }, [tabItems]);

  const handleAddRDeviceTab = useCallback((device: Device) => {
    const deviceId = device?.id;
    const tab = getDeviceTab(device, tabItems?.length);
    const existTab = getIdentifierExists(deviceId);
    if (isEmpty(existTab)) {
      dispatch(updateDevicesTab(tab));
    } else {
      getDeviceExistMessage(deviceId);
    }
    dispatch(setSelectedDevicesTab(device));
  }, [tabItems]);

  const getMaxDeviceMessage = () => {
    dispatch(toggleUserPrompt({
      message     : translate('admin-devices:dialogBulkView.maxViewDevices', {count: MAX_VIEW_DEVICES}),
      actionLabel : translate('common:ok'),
      autoHide    : false
    }));
  };

  const getDeviceExistMessage = (deviceId) => {
    dispatch(toggleUserPrompt({
      message     : translate('admin-devices:dialogBulkView.deviceExists', {device: deviceId}),
      actionLabel : translate('common:ok'),
      autoHide    : false
    }));
  };

  return {
    tabItems,
    selectedIndex,
    selectedTab,
    handleOnChange,
    onCloseTab,
    getIdentifierExists,
    handleAddRDeviceTab,
    getMaxDeviceMessage,
    getDeviceExistMessage
  };
};

export default useBulkViewDeviceTabs;
